import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class TranslationMappingService {
  private titleToKeyMap = new Map<string, string>([
    // Navigation items
    ['Dashboard', 'NAVIGATION.DASHBOARD'],
    ['Appointments', 'NAVIGATION.APPOINTMENTS'],
    ['Packages', 'NAVIGATION.PACKAGES'],
    ['Enrollments', 'NAVIGATION.ENROLLMENTS'],
    ['Payments', 'NAVIGATION.PAYMENTS'],
    ['Students', 'NAVIGATION.STUDENTS'],
    ['Reports', 'NAVIGATION.REPORTS'],
    ['Configuration', 'NAVIGATION.CONFIGURATION'],

    // Appointments sub-menu
    ['Day View', 'APPOINTMENTS.DAY_VIEW'],
    ['Week View', 'APPOINTMENTS.WEEK_VIEW'],
    ['Appointment List', 'APPOINTMENTS.APPOINTMENT_LIST'],

    // Packages sub-menu
    ['View Packages', 'PACKAGES.VIEW_PACKAGES'],

    // Enrollments sub-menu
    ['View Enrollments', 'ENROLLMENTS.VIEW_ENROLLMENTS'],
    ['Add Enrollment', 'ENROLLMENTS.ADD_ENROLLMENT'],

    // Payments sub-menu
    ['View Payments', 'PAYMENTS.VIEW_PAYMENTS'],
    ['Add Payment', 'PAYMENTS.ADD_PAYMENT'],

    // Students sub-menu
    ['Student List', 'STUDENTS.STUDENT_LIST'],
    ['Add Student Account', 'STUDENTS.ADD_STUDENT_ACCOUNT'],

    // Reports sub-menu
    ['AMI', 'REPORTS.AMI'],
    ['Cash Report', 'REPORTS.CASH_REPORT'],
    ['Payments Due', 'REPORTS.PAYMENTS_DUE'],
    ['Payments Past Due', 'REPORTS.PAYMENTS_PAST_DUE'],
    ['Inquiry Data', 'REPORTS.INQUIRY_DATA'],
    ['Student Information', 'REPORTS.STUDENT_INFORMATION'],
    ['Lesson History', 'REPORTS.LESSON_HISTORY'],
    ['Tax Report', 'REPORTS.TAX_REPORT'],
    ['Cash Report Detailed', 'REPORTS.CASH_REPORT_DETAILED'],
    ['Past Due Report', 'REPORTS.PAST_DUE_REPORT'],
    ['Attendance Report', 'REPORTS.ATTENDANCE_REPORT'],
    ['Lesson Tracking', 'REPORTS.LESSON_TRACKING'],
    ['Trial Lesson Report', 'REPORTS.TRIAL_LESSON_REPORT'],
    ['Makeup Lesson Report', 'REPORTS.MAKEUP_LESSON_REPORT'],
    ['Enrollment Report', 'REPORTS.ENROLLMENT_REPORT'],
    ['Student Count', 'REPORTS.STUDENT_COUNT'],
    ['Rate Report', 'REPORTS.RATE_REPORT'],
    ['Teacher Schedule', 'REPORTS.TEACHER_SCHEDULE'],
    ['Room Schedule', 'REPORTS.ROOM_SCHEDULE'],
    ['Financial Summary', 'REPORTS.FINANCIAL_SUMMARY'],
    ['Revenue Report', 'REPORTS.REVENUE_REPORT'],
    ['Expense Report', 'REPORTS.EXPENSE_REPORT'],
    ['Profit Loss Report', 'REPORTS.PROFIT_LOSS_REPORT'],
    ['Balance Sheet', 'REPORTS.BALANCE_SHEET'],
    ['Custom Report', 'REPORTS.CUSTOM_REPORT'],

    // Configuration sub-menu
    ['Studio Information', 'CONFIGURATION.STUDIO_INFORMATION'],
    ['Staff Management', 'CONFIGURATION.STAFF_MANAGEMENT'],
    ['Integrations', 'CONFIGURATION.INTEGRATIONS'],
    ['System Settings', 'CONFIGURATION.SYSTEM_SETTINGS'],
    ['Backup & Restore', 'CONFIGURATION.BACKUP_RESTORE'],
    ['User Permissions', 'CONFIGURATION.USER_PERMISSIONS'],
    ['Email Templates', 'CONFIGURATION.EMAIL_TEMPLATES'],
    ['Payment Settings', 'CONFIGURATION.PAYMENT_SETTINGS'],
    ['Calendar Settings', 'CONFIGURATION.CALENDAR_SETTINGS'],
    ['Notification Settings', 'CONFIGURATION.NOTIFICATION_SETTINGS'],

    // Common actions
    ['Add', 'COMMON.ADD'],
    ['Edit', 'COMMON.EDIT'],
    ['Delete', 'COMMON.DELETE'],
    ['Save', 'COMMON.SAVE'],
    ['Cancel', 'COMMON.CANCEL'],
    ['Search', 'COMMON.SEARCH'],
    ['Filter', 'COMMON.FILTER'],
    ['Export', 'COMMON.EXPORT'],
    ['Print', 'COMMON.PRINT'],
    ['View', 'COMMON.VIEW'],
    ['Close', 'COMMON.CLOSE'],
    ['Submit', 'COMMON.SUBMIT'],
    ['Reset', 'COMMON.RESET'],
    ['Back', 'COMMON.BACK'],
    ['Next', 'COMMON.NEXT'],
    ['Previous', 'COMMON.PREVIOUS'],
    ['Refresh', 'COMMON.REFRESH'],
    ['Loading', 'COMMON.LOADING'],
    ['No Data', 'COMMON.NO_DATA'],
    ['Select All', 'COMMON.SELECT_ALL'],
    ['Clear All', 'COMMON.CLEAR_ALL'],

    // Dashboard
    ['Contact', 'DASHBOARD.STUDENT_SUMMARY.CONTACT'],
    ['Booked', 'DASHBOARD.STUDENT_SUMMARY.BOOKED'],
    ['Showed', 'DASHBOARD.STUDENT_SUMMARY.SHOWED'],
    ['YTD', 'DASHBOARD.STUDENT_SUMMARY.YTD'],

    // Footer
    ['Arthur Murray International. All rights reserved.', 'FOOTER.COPYRIGHT'],
    ['v', 'FOOTER.VERSION']
  ]);

  /**
   * Gets the translation key for a given title
   * @param title - The original title text
   * @returns The translation key or the original title if no mapping exists
   */
  getTranslationKey(title: string): string {
    return this.titleToKeyMap.get(title) || title;
  }

  /**
   * Checks if a mapping exists for the given title
   * @param title - The title to check
   * @returns True if mapping exists, false otherwise
   */
  hasMapping(title: string): boolean {
    return this.titleToKeyMap.has(title);
  }

  /**
   * Adds a new mapping or updates an existing one
   * @param title - The title text
   * @param key - The translation key
   */
  addMapping(title: string, key: string): void {
    this.titleToKeyMap.set(title, key);
  }

  /**
   * Removes a mapping
   * @param title - The title to remove
   */
  removeMapping(title: string): void {
    this.titleToKeyMap.delete(title);
  }

  /**
   * Gets all mappings as an array of [title, key] pairs
   * @returns Array of mapping pairs
   */
  getAllMappings(): [string, string][] {
    return Array.from(this.titleToKeyMap.entries());
  }
}
